import { ComponentRegistryConsumer } from '@bem-react/di';
//@ts-ignore
import { CLESSON_MODES } from '@yandex-int/k-common/constants';
import { EXTERNAL_LMS_SLUGS, LESSON_STATUS } from '@yandex-int/k-common/typings';
import { Spin, Text } from 'platform-components';
import React from 'react';
import { WrappedComponentProps } from 'react-intl';

import AccordionItem from 'common.components/accordion/__item/accordion__item';
import Accordion from 'common.components/accordion/accordion';
import type { AccordionItemData } from 'common.components/accordion/typings';
import { ACCORDION_ITEMS } from 'common.components/lesson-view.assignment/typings';
import { cnLessonView } from 'common.components/lesson-view.index/registry';
import { STUDENTS_ERRORS } from 'common.components/students-list/typings';

import { CollapsedMes } from '../__collapsed/assignment__collapsed_mes';
import { AssignmentDateMode } from '../__date-mode/assignment__date-mode';
import { AssignmentDatePlaceholder } from '../__date-placeholder/assignment__date-placeholder';
import { AssignmentLessonTypeMode } from '../__lesson-type-mode/assignment__lesson-type-mode';
import { AssignmentLessonTypePlaceholder } from '../__lesson-type-placeholder/assignment__lesson-type-placeholder';
import { AssignmentStudentsMode } from '../__students-mode/assignment__students-mode';
import { AssignmentStudentsPlaceholder } from '../__students-placeholder/assignment__students-placeholder';

interface Props extends WrappedComponentProps {
    activeSubscriptionSlug: string | null;
    canAssignIndividually: boolean;
    currentAccordionItem: ACCORDION_ITEMS | null;
    isEmptyTeam: boolean;
    inStudentLinkExperiment: boolean;
    isLessonTypeModeDisabled: boolean;
    shouldHideLessonMode: boolean;
    shouldShowStudentsLoader: boolean;
    shouldShowLmsLoader: boolean;
    lmsSlug: EXTERNAL_LMS_SLUGS | null;
    onAccordionItemClick: (item: ACCORDION_ITEMS) => void;
    lessonMode: CLESSON_MODES;
    selectedStudents: Array<number>;
    studentsError: STUDENTS_ERRORS | null;
    isAllTeamCheckboxDisabled: boolean;
    onToggleStudentAssignment: (selected: Array<number>) => void;
    onSelectMode: (mode: CLESSON_MODES) => void;
    teamId: number;
    canChangeAssignmentDates: boolean;
    assignDateValue: Date | null;
    deadlineDateValue: Date | null;
    canEditTeam: boolean;
    onAddStudentsClick: () => void;
    onSendStudentLinkClick: () => void;
    assignmentTable: Array<string>;
    lessonStatus: LESSON_STATUS;
    schoolId: number | null;
    isTeamFromMes: boolean;
    isTeacherFromMes: boolean;
    savedOrFirstTeamId: number | null;
}

// eslint-disable-next-line complexity
export const AssignmentMainSettings = (props: Props) => {
    const {
        activeSubscriptionSlug,
        canAssignIndividually,
        currentAccordionItem,
        intl,
        isEmptyTeam,
        shouldHideLessonMode,
        shouldShowLmsLoader,
        lmsSlug,
        onAccordionItemClick,
        selectedStudents,
        isAllTeamCheckboxDisabled,
        inStudentLinkExperiment,
        isLessonTypeModeDisabled,
        onToggleStudentAssignment,
        studentsError,
        lessonMode,
        shouldShowStudentsLoader,
        teamId,
        onSelectMode,
        canChangeAssignmentDates,
        assignDateValue,
        deadlineDateValue,
        canEditTeam,
        onAddStudentsClick,
        onSendStudentLinkClick,
        assignmentTable,
        lessonStatus,
        schoolId,
        isTeamFromMes,
        isTeacherFromMes,
        savedOrFirstTeamId,
    } = props;

    if (shouldShowLmsLoader) {
        return (
            <div className="assignment__lms-data-loading">
                <Spin mix="assignment__lms-data-loading-spin" />
                <Text id={`lessonView.assignment.lms.loadingDataFrom.${lmsSlug}`} />
            </div>
        );
    }

    const items: AccordionItemData[] = [];

    const studentsItem = {
        id: ACCORDION_ITEMS.STUDENTS,
        item: {
            fullContent: (
                <AssignmentStudentsMode
                    assignmentTable={assignmentTable}
                    canAssignIndividually={canAssignIndividually}
                    canEditTeam={canEditTeam}
                    inStudentLinkExperiment={inStudentLinkExperiment}
                    isAllTeamCheckboxDisabled={isAllTeamCheckboxDisabled}
                    isTeamFromMes={isTeamFromMes}
                    lessonMode={lessonMode}
                    lessonStatus={lessonStatus}
                    onAddStudentsClick={onAddStudentsClick}
                    onSendStudentLinkClick={onSendStudentLinkClick}
                    onToggleStudentAssignment={onToggleStudentAssignment}
                    selectedStudents={selectedStudents}
                    studentsError={studentsError}
                />
            ),
            previewContent: (
                <AssignmentStudentsPlaceholder
                    selectedStudents={selectedStudents}
                    shouldShowStudentsLoader={shouldShowStudentsLoader}
                    teamId={teamId}
                />
            ),
        },
        title: intl.formatMessage({ id: 'lessonView.assignment.accordionItem.students' }),
        onItemClick: isEmptyTeam || shouldShowStudentsLoader ? undefined : onAccordionItemClick,
    };

    if (!shouldHideLessonMode && !isTeacherFromMes) {
        items.push({
            id: ACCORDION_ITEMS.MODE,
            item: {
                fullContent: (
                    <AssignmentLessonTypeMode
                        disabled={isLessonTypeModeDisabled}
                        lessonMode={lessonMode}
                        onSelectMode={onSelectMode}
                    />
                ),
                previewContent: <AssignmentLessonTypePlaceholder lessonMode={lessonMode} />,
            },
            title: intl.formatMessage({ id: 'lessonView.assignment.accordionItem.mode' }),
            onItemClick: onAccordionItemClick,
        });
    }

    if (!isTeacherFromMes) {
        items.push({
            id: ACCORDION_ITEMS.DATES,
            item: {
                fullContent: (
                    <AssignmentDateMode
                        activeSubscriptionSlug={activeSubscriptionSlug}
                        canChangeAssignmentDates={canChangeAssignmentDates}
                        lessonMode={lessonMode}
                    />
                ),
                previewContent: (
                    <AssignmentDatePlaceholder
                        assignDateValue={assignDateValue}
                        deadlineDateValue={deadlineDateValue}
                    />
                ),
            },
            title: intl.formatMessage({ id: 'lessonView.assignment.accordionItem.dates' }),
            onItemClick: onAccordionItemClick,
        });
    } else {
        // датой выдачи учитель управляет из МЭШ
        items.push({
            id: ACCORDION_ITEMS.DATES,
            item: {
                fullContent: null,
                previewContent: <CollapsedMes />,
            },
            title: intl.formatMessage({ id: 'lessonView.assignment.accordionItem.dates' }),
            isWithIcon: false,
        });
    }

    if (isTeacherFromMes) {
        items.push({
            id: ACCORDION_ITEMS.STUDENTS,
            item: {
                fullContent: null,
                previewContent: (
                    <AssignmentStudentsPlaceholder
                        selectedStudents={selectedStudents}
                        shouldShowStudentsLoader={shouldShowStudentsLoader}
                        teamId={teamId}
                    />
                ),
            },
            title: intl.formatMessage({ id: 'lessonView.assignment.accordionItem.students' }),
            isWithIcon: false,
        });
    } else if (teamId && !isEmptyTeam) {
        items.push(studentsItem);
    }

    const showError =
        studentsError === STUDENTS_ERRORS.EMPTY_SCHOOL_AND_TEAM || studentsError === STUDENTS_ERRORS.EMPTY_SCHOOL;

    return (
        <>
            <Accordion expandedItem={currentAccordionItem} items={items} />
            {isEmptyTeam && schoolId && (
                <div className="assignment__empty-list">
                    <AccordionItem {...studentsItem} isExpanded={true} isWithIcon={false} />
                </div>
            )}
            <ComponentRegistryConsumer id={cnLessonView()}>
                {({ AssignmentAddSchoolControl }) =>
                    (!schoolId || !savedOrFirstTeamId) && <AssignmentAddSchoolControl showError={showError} />
                }
            </ComponentRegistryConsumer>
        </>
    );
};
